/* Thin client for the add-on integration endpoints on the PEAR backend (the
   same Render "doorman" service that handles payments). The browser NEVER calls
   GPS/fuel/fee vendors directly — it asks this backend, which holds the org's
   credentials server-side and makes the real call. Base URL is the shared
   REACT_APP_PAYMENTS_URL; when unset, callers fail-open (app stays in default).

   Every function is fail-open by design: on any error it resolves to an
   { ok:false, message } the caller can surface, rather than throwing. */

import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

/** Standard "something is wrong, it's not the user's fault" copy. */
export const TECH_ISSUE_MESSAGE =
  'There’s a technical issue retrieving data from the provider right now. The app is running normally in the meantime — please contact the technical team.';

export function isAddonsConfigured(): boolean {
  return !!BASE;
}

export type AddonKey = 'gps' | 'fuel' | 'fees';

export interface AddonTestResult {
  ok: boolean;
  sampleCount: number;
  message: string;
}

export interface FuelReading {
  plate: string;
  /** Period / fill volume when the vendor reports consumption — theft compare. */
  liters?: number;
  /** Absolute litres in the tank when known — Engine source of truth. */
  litersInTank?: number;
  /** 0..1 tank fraction when the vendor reports percent. */
  fillFraction?: number;
}
export interface GpsReading { plate: string; odometerKm?: number; lat?: number; lng?: number; }
export interface FeeRecord { matchKey: string; matchBy: 'email' | 'parentName' | 'childName'; paid: boolean; balance: number; }

export interface AddonPullResult<T> {
  ok: boolean;
  data: T[];
  message: string;
}

async function authedPost(path: string, body: unknown): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('not-signed-in');
  return fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
}

/** Verify reachability + credentials for one add-on (the "Save & verify" gate). */
export async function testAddonConnection(orgCode: string, addonKey: AddonKey): Promise<AddonTestResult> {
  if (!BASE) return { ok: false, sampleCount: 0, message: 'The integration backend is not configured yet, so live connections can’t be tested. Contact the technical team.' };
  try {
    const res = await authedPost('/api/addons/test', { orgCode, addonKey });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, sampleCount: 0, message: json.error || json.message || TECH_ISSUE_MESSAGE };
    return { ok: !!json.ok, sampleCount: json.sampleCount || 0, message: json.message || (json.ok ? 'Connection verified.' : TECH_ISSUE_MESSAGE) };
  } catch {
    return { ok: false, sampleCount: 0, message: TECH_ISSUE_MESSAGE };
  }
}

/** Pull live data for one add-on. Resolves ok:false (never throws) on any failure. */
export async function pullAddonData<T>(orgCode: string, addonKey: AddonKey): Promise<AddonPullResult<T>> {
  if (!BASE) return { ok: false, data: [], message: 'The integration backend is not configured.' };
  try {
    const res = await authedPost('/api/addons/pull', { orgCode, addonKey });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, data: [], message: json.error || json.message || TECH_ISSUE_MESSAGE };
    return { ok: !!json.ok, data: Array.isArray(json.data) ? json.data : [], message: json.message || '' };
  } catch {
    return { ok: false, data: [], message: TECH_ISSUE_MESSAGE };
  }
}

/**
 * Ask the backend to run the comparison sweep for this org NOW.
 *
 * The sweep itself lives on the 15-minute cron (`engine/addons/sweep.js`) and is
 * what actually keeps a connected system flowing. This is only the "Sync now"
 * button: the engine deliberately does NOT exist in the browser any more, so
 * there is no second implementation to drift out of step with the scheduled one
 * — and no way to end up, as before, with the integration running nowhere except
 * inside one staff-only page that happened to be open.
 */
export async function syncAddonNow(
  orgCode: string,
  addonKey?: AddonKey,
): Promise<{ ok: boolean; message: string }> {
  if (!BASE) return { ok: false, message: 'The integration backend is not configured.' };
  try {
    const res = await authedPost('/api/addons/sync', { orgCode, ...(addonKey ? { addonKey } : {}) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) return { ok: false, message: json.message || json.error || TECH_ISSUE_MESSAGE };
    return { ok: true, message: 'Sync complete.' };
  } catch {
    return { ok: false, message: TECH_ISSUE_MESSAGE };
  }
}

/** Save an add-on's vendor credentials SERVER-SIDE (into the deny-all addonSecrets
 *  subcollection). The browser must NOT write apiKey/apiUrl/extraFields to the
 *  world-readable org doc anymore (C5). */
export async function saveAddonCredentials(
  orgCode: string,
  addonKey: AddonKey,
  creds: { provider: string; apiUrl: string; apiKey: string; extraFields: Record<string, string> },
): Promise<{ ok: boolean; message?: string }> {
  if (!BASE) return { ok: false, message: 'The integration backend is not configured, so credentials can’t be saved securely.' };
  try {
    const res = await authedPost('/api/addons/credentials', { orgCode, addonKey, ...creds });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not save credentials.' };
    return { ok: !!json.ok };
  } catch {
    return { ok: false, message: 'Could not reach the backend to save credentials.' };
  }
}
